import { useCallback, useEffect, useState } from "react";
import type { UserDto } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatDateTime } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { CreateUserDialog } from "../components/users/createUserDialog.js";
import { ManageUserCell } from "../components/users/manageUserCell.js";
import { useI18n, type MessageKey } from "../i18n/index.js";

export function UsersPage() {
  const { t } = useI18n();
  const { user: me } = useSession();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(
    null
  );
  const [items, setItems] = useState<UserDto[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listUsers();
      setItems(res.items);
    } catch (err) {
      const e = err as ApiClientError;
      setError({ message: e.message, requestId: e.requestId });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<UserDto>[] = [
    {
      key: "name",
      header: t("users.column.user"),
      render: (row) => (
        <span>
          <span className="font-medium text-ink">{row.displayName}</span>
          <span className="block text-xs text-ink-muted">{row.email}</span>
        </span>
      ),
    },
    {
      key: "role",
      header: t("users.role"),
      render: (row) => (
        <Badge tone="neutral">{t(`roles.${row.roleKey}` as MessageKey)}</Badge>
      ),
    },
    {
      key: "status",
      header: t("common.status"),
      render: (row) => (
        <Badge tone={row.active ? "ready" : "risk"}>
          {row.active ? t("users.active") : t("users.inactive")}
        </Badge>
      ),
    },
    {
      key: "lastLogin",
      header: t("users.column.lastSignIn"),
      render: (row) => <span className="tabular">{formatDateTime(row.lastLoginAt)}</span>,
    },
    {
      key: "actions",
      header: t("users.column.manage"),
      render: (row) => (
        <ManageUserCell
          row={row}
          isSelf={row.id === me?.id}
          onChanged={() => void load()}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("nav.users")}
        description={t("users.description")}
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t("users.addUser")}
          </Button>
        }
      />

      <DataTable
        caption={t("users.caption")}
        loading={loading}
        error={error}
        onRetry={load}
        rows={items}
        rowKey={(row) => row.id}
        columns={columns}
        emptyTitle={t("users.emptyTitle")}
        emptyDescription={t("users.emptyDescription")}
      />

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => void load()}
      />
    </>
  );
}
